import { useState } from "react";
import { motion } from "framer-motion";
import CountUp from "react-countup";
import { Zap, ArrowRight, Leaf } from "lucide-react";
import api from "@/lib/api";
import { Reveal, GMark } from "@/components/Reveal";

const money = (v) => v.toLocaleString("pt-BR", { style: "currency", currency: "BRL", maximumFractionDigits: 0 });

export default function Simulator({ onLead }) {
  const [bill, setBill] = useState(500);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/simulate", { bill_value: Number(bill) });
      setResult(data);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="simulador" className="relative py-24 sm:py-32 bg-background overflow-hidden">
      <GMark className="h-[420px] -left-24 -bottom-12 -rotate-6" opacity={0.045} />
      <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
        <Reveal>
          <p className="overline text-primary mb-4">Calculadora de economia</p>
          <h2 className="font-display font-bold text-4xl sm:text-6xl tracking-tighter text-secondary max-w-2xl leading-[0.95]">
            Descubra quanto você pode economizar
          </h2>
        </Reveal>

        <div className="mt-14 grid lg:grid-cols-2 gap-8 items-stretch">
          {/* Input card */}
          <Reveal>
            <div className="tracing-border rounded-3xl p-8 sm:p-10 h-full">
              <div className="flex items-center gap-2 text-primary mb-6">
                <Zap size={20} />
                <span className="font-bold text-secondary">Valor médio da sua conta de luz</span>
              </div>

              <div className="text-5xl sm:text-6xl font-display font-extrabold text-secondary tracking-tight mb-2">
                {money(Number(bill))}
              </div>
              <p className="text-sm text-muted-foreground mb-8">por mês</p>

              <input
                type="range"
                min="100"
                max="5000"
                step="50"
                value={bill}
                onChange={(e) => setBill(e.target.value)}
                className="w-full accent-primary h-2 cursor-pointer"
                data-testid="simulator-slider"
              />
              <div className="flex justify-between text-xs text-muted-foreground mt-2">
                <span>R$ 100</span><span>R$ 5.000</span>
              </div>

              <button
                onClick={run}
                disabled={loading}
                className="mt-8 w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full bg-primary text-primary-foreground font-bold hover:brightness-105 active:scale-[0.98] transition-all disabled:opacity-60"
                data-testid="simulator-submit-btn"
              >
                {loading ? "Calculando..." : "Calcular economia"} <ArrowRight size={18} />
              </button>
            </div>
          </Reveal>

          {/* Result card */}
          <Reveal delay={0.1}>
            <div className="rounded-3xl bg-secondary text-secondary-foreground p-8 sm:p-10 h-full grain relative overflow-hidden">
              <p className="overline text-accent mb-8">Sua economia estimada</p>

              {result ? (
                <div className="space-y-7" data-testid="simulator-result">
                  <Stat label="Economia mensal" value={result.monthly_savings} accent />
                  <Stat label="Economia anual" value={result.annual_savings} />
                  <Stat label="Em 25 anos" value={result.savings_25y} big />
                  <div className="flex items-center gap-2 text-sm text-secondary-foreground/70 pt-2 border-t border-white/10">
                    <Leaf size={16} className="text-primary" />
                    <span>
                      ~<CountUp end={result.co2_year_kg} decimals={0} duration={1.2} separator="." /> kg de CO₂ evitados por ano
                    </span>
                  </div>
                  <button
                    onClick={() => onLead(Number(bill))}
                    className="w-full mt-2 px-6 py-4 rounded-full bg-accent text-accent-foreground font-bold hover:brightness-105 active:scale-[0.98] transition-all"
                    data-testid="simulator-lead-btn"
                  >
                    Quero receber meu orçamento
                  </button>
                </div>
              ) : (
                <div className="h-full flex flex-col justify-center text-secondary-foreground/60 py-16">
                  <p className="text-lg">Ajuste o valor ao lado e clique em <span className="text-primary font-semibold">calcular</span> para ver sua economia em 25 anos.</p>
                </div>
              )}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function Stat({ label, value, accent, big }) {
  return (
    <div>
      <p className="text-sm text-secondary-foreground/60 mb-1">{label}</p>
      <p className={`font-display font-extrabold tracking-tight ${big ? "text-5xl sm:text-6xl" : "text-3xl sm:text-4xl"} ${accent ? "text-primary" : "text-secondary-foreground"}`}>
        R$ <CountUp end={value} duration={1.2} separator="." decimals={0} />
      </p>
    </div>
  );
}
